import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Dumbbell, ArrowLeft } from 'lucide-react';
import { LEGAL } from '@/config/legal';

export const LegalLinks: React.FC<{ className?: string }> = ({ className = '' }) => (
  <nav aria-label="Legal" className={`flex flex-wrap gap-x-5 gap-y-2 ${className}`}>
    <Link to="/privacy" className="hover:underline">Privacy Policy</Link>
    <Link to="/terms" className="hover:underline">Terms of Service</Link>
    <Link to="/credits" className="hover:underline">Credits</Link>
  </nav>
);

interface LegalLayoutProps {
  title: string;
  intro?: React.ReactNode;
  children: React.ReactNode;
}

/** Shared public layout for the Privacy, Terms and Credits pages. */
const LegalLayout: React.FC<LegalLayoutProps> = ({ title, intro, children }) => {
  useEffect(() => {
    document.title = `${title} · ${LEGAL.appName}`;
    window.scrollTo(0, 0);
    return () => { document.title = LEGAL.appName; };
  }, [title]);

  return (
    <div className="min-h-screen bg-app text-ink">
      <header className="border-b border-line">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold">
            <Dumbbell className="h-6 w-6 text-accent" />
            {LEGAL.appName}
          </Link>
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-ink-dim hover:text-ink">
            <ArrowLeft className="h-4 w-4" /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="text-3xl sm:text-4xl font-black mb-2">{title}</h1>
        <p className="text-sm text-ink-dim mb-6">Effective {LEGAL.effectiveDate}</p>
        {intro && <div className="text-ink-muted leading-relaxed mb-8">{intro}</div>}
        <div className="legal-prose space-y-8 text-ink-muted leading-relaxed">{children}</div>
      </main>

      <footer className="border-t border-line">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between text-sm text-ink-dim">
          <LegalLinks />
          <span>© {new Date().getFullYear()} {LEGAL.appName}</span>
        </div>
      </footer>
    </div>
  );
};

export const Section: React.FC<{ id?: string; title: string; children: React.ReactNode }> = ({ id, title, children }) => (
  <section id={id} className="space-y-3 scroll-mt-6">
    <h2 className="text-xl font-bold text-ink">{title}</h2>
    {children}
  </section>
);

export const Mail: React.FC = () => (
  <a href={`mailto:${LEGAL.contactEmail}`} className="text-accent underline">{LEGAL.contactEmail}</a>
);

export default LegalLayout;
